/**
 * V1.8.8 — Wall segment + material helpers for FloorPlan2D.
 *
 * The Simple-Mode blueprint doesn't ship explicit wall geometry by default —
 * each room is just an axis-aligned rectangle (x, z, width, depth). For the
 * Architectural plan view we need to draw wall SEGMENTS with proper thickness
 * + material hatching (wood frame, steel stud, CMU, brick, concrete), plus a
 * Wall Type Schedule legend so the symbols are decoded for the reader.
 *
 * Two helpers exported:
 *   1. computeWallSegments(rooms, wallTypes)
 *      Walks every room edge, classifies each edge as either EXTERIOR (no
 *      neighbour on that line) or INTERIOR (shared with another room),
 *      assigns a material per the wall_types config (with sensible defaults
 *      when missing), and returns {axis, coord, span_lo, span_hi, kind,
 *      material, label, thickness_m, tag} entries — purely geometric, the
 *      caller handles SVG drawing.
 *   2. defaultWallTypes() — base config used when AI omits wall_types.
 *
 * The material catalog lives here (single source of truth for hatch IDs,
 * colors, thicknesses, labels) so FloorPlan2D + WallTypeLegend stay in sync.
 */

const TOL = 0.05; // metres — adjacency tolerance, same as openings.js

// Catalog of supported materials. Keep in sync with WallTypeLegend.jsx and
// the SVG <defs> in FloorPlan2D.
//   thickness_ext / thickness_int: realistic wall thickness in METRES.
//   hatch:   id of the SVG pattern (defined in FloorPlan2D <defs>).
//   color:   hatch line + tag accent color.
//   label:   human-readable schedule label.
//   tag_prefix: prefix used in the schedule tag (WT1, WT2, …).
export const MATERIAL_CATALOG = {
  wood_frame: {
    thickness_ext: 0.18, thickness_int: 0.12,
    hatch: "hatch-wood",  color: "#1f2937",
    label: "Wood Frame 2x6",
  },
  steel_stud: {
    thickness_ext: 0.20, thickness_int: 0.14,
    hatch: "hatch-steel", color: "#374151",
    label: "Steel Stud 6\"",
  },
  cmu: {
    thickness_ext: 0.30, thickness_int: 0.20,
    hatch: "hatch-cmu",   color: "#52525b",
    label: "CMU Block 8\"",
  },
  brick: {
    thickness_ext: 0.32, thickness_int: 0.20,
    hatch: "hatch-brick", color: "#7c2d12",
    label: "Brick Veneer + Frame",
  },
  concrete: {
    thickness_ext: 0.25, thickness_int: 0.18,
    hatch: "hatch-concrete", color: "#3f3f46",
    label: "Cast-in-Place Concrete",
  },
};

const VALID = (m) =>
  m && Object.prototype.hasOwnProperty.call(MATERIAL_CATALOG, String(m).toLowerCase())
    ? String(m).toLowerCase()
    : null;

export function defaultWallTypes() {
  return { exterior: "wood_frame", interior: "wood_frame", segments: [] };
}

/**
 * Resolve material assignment for a given wall edge.
 *   kind: "exterior" | "interior"
 *   a / b: the room ids on either side (b may be null for exterior)
 * Lookup priority:
 *   1. wallTypes.segments[*] match on {from_room, to_room} (un-ordered) —
 *      lets the AI override a specific partition (e.g. acoustic wall).
 *   2. wallTypes[kind] default.
 *   3. defaultWallTypes()[kind].
 */
function resolveMaterial(wallTypes, kind, a, b) {
  const defaults = defaultWallTypes();
  const root = wallTypes || defaults;
  if (kind === "interior" && Array.isArray(root.segments) && a && b) {
    const key = [a, b].sort().join("|");
    for (const seg of root.segments) {
      if (!seg) continue;
      const sa = seg.from_room || seg.room_a;
      const sb = seg.to_room || seg.room_b;
      if (sa && sb && [sa, sb].sort().join("|") === key) {
        const m = VALID(seg.material);
        if (m) return m;
      }
    }
  }
  return VALID(root[kind]) || defaults[kind];
}

/**
 * Compute every wall segment for the floor.
 *
 * Returns an array of:
 *   { id, axis: 'x'|'z', coord, span_lo, span_hi, kind, material,
 *     label, thickness_m, tag, a_id, b_id }
 *  - axis 'x' = wall runs along x (so it's a horizontal line in plan)
 *  - axis 'z' = wall runs along z (vertical line in plan)
 *  - thickness_m: wall thickness in METRES; caller multiplies by pixel scale
 *  - tag: schedule tag string like "WT1" (incremented per unique material)
 */
/**
 * V2.0.9 — "Merged rooms" support for L-shaped / open-plan layouts.
 * When two rooms have each other in their `merged_with: string[]` array,
 * the shared interior wall between them is OMITTED so the area reads
 * as a single continuous room in 2D, 3D, and the architectural PDF.
 *
 * The merge relation is symmetric — we accept it from either side
 * (`r1.merged_with: ["r2"]` or `r2.merged_with: ["r1"]` is enough).
 */
function isMergedPair(roomA, roomB) {
  if (!roomA || !roomB || roomA.id === roomB.id) return false;
  const mA = Array.isArray(roomA.merged_with) ? roomA.merged_with : [];
  const mB = Array.isArray(roomB.merged_with) ? roomB.merged_with : [];
  return mA.includes(roomB.id) || mB.includes(roomA.id);
}

export function computeWallSegments(rooms, wallTypes) {
  const wt = wallTypes || defaultWallTypes();
  const valid = (rooms || []).filter(
    (r) => r && r.width > 0 && r.depth > 0 &&
      Number.isFinite(r.x) && Number.isFinite(r.z),
  );

  // Build edge candidates. Each candidate is a directed (room, side) tuple.
  const candidates = [];
  valid.forEach((r) => {
    candidates.push({ room: r, axis: "x", coord: r.z,            span_lo: r.x, span_hi: r.x + r.width });
    candidates.push({ room: r, axis: "x", coord: r.z + r.depth,  span_lo: r.x, span_hi: r.x + r.width });
    candidates.push({ room: r, axis: "z", coord: r.x,            span_lo: r.z, span_hi: r.z + r.depth });
    candidates.push({ room: r, axis: "z", coord: r.x + r.width,  span_lo: r.z, span_hi: r.z + r.depth });
  });

  const segments = [];
  candidates.forEach((c) => {
    // Find all OTHER candidates on the same axis+coord line.
    const overlaps = candidates.filter(
      (o) =>
        o !== c &&
        o.room !== c.room &&
        o.axis === c.axis &&
        Math.abs(o.coord - c.coord) < TOL,
    );
    // Walk the [span_lo, span_hi] range, splitting wherever an overlap starts
    // or ends. Each resulting sub-segment is either exterior (no overlap) or
    // interior (overlapped with exactly one neighbour at that x/z range).
    const breakpoints = new Set([c.span_lo, c.span_hi]);
    overlaps.forEach((o) => {
      if (o.span_lo > c.span_lo && o.span_lo < c.span_hi) breakpoints.add(o.span_lo);
      if (o.span_hi > c.span_lo && o.span_hi < c.span_hi) breakpoints.add(o.span_hi);
    });
    const sorted = Array.from(breakpoints).sort((a, b) => a - b);
    for (let i = 0; i < sorted.length - 1; i++) {
      const lo = sorted[i];
      const hi = sorted[i + 1];
      if (hi - lo < 0.2) continue; // ignore sub-cm slivers
      const mid = (lo + hi) / 2;
      const neighbour = overlaps.find(
        (o) => o.span_lo - TOL < mid && o.span_hi + TOL > mid,
      );
      // V2.0.9 — Skip this sub-segment entirely if the two rooms are
      // merged. Both sides of the wall will skip via the same logic,
      // so no wall ever renders between them.
      if (neighbour && isMergedPair(c.room, neighbour.room)) {
        continue;
      }
      if (neighbour && neighbour.room.id < c.room.id) {
        // Deduplicate — only emit one record per shared edge.
        continue;
      }
      const kind = neighbour ? "interior" : "exterior";
      const material = resolveMaterial(wt, kind, c.room.id, neighbour?.room.id || null);
      const cat = MATERIAL_CATALOG[material];
      segments.push({
        axis: c.axis,
        coord: c.coord,
        span_lo: lo,
        span_hi: hi,
        kind,
        material,
        label: cat.label,
        thickness_m: kind === "exterior" ? cat.thickness_ext : cat.thickness_int,
        a_id: c.room.id,
        b_id: neighbour?.room.id || null,
      });
    }
  });

  // Assign stable WT-N tags per UNIQUE material+kind combo so the schedule
  // can show "WT1 = Brick Exterior · 12in" rather than tagging every single
  // segment individually. Order = first-seen.
  const tagByKey = new Map();
  let tagSeq = 1;
  segments.forEach((s) => {
    const key = `${s.material}|${s.kind}`;
    if (!tagByKey.has(key)) {
      tagByKey.set(key, `WT${tagSeq++}`);
    }
    s.tag = tagByKey.get(key);
    s.id = `${s.tag}-${s.axis}${Math.round(s.coord * 10)}-${Math.round(s.span_lo * 10)}`;
  });
  return segments;
}

/**
 * Derive the Wall Type Schedule from a computed segments list — one row per
 * unique material+kind combo. Used by WallTypeLegend.
 *   Returns: [{ tag, material, kind, label, thickness_m }]
 */
export function buildWallTypeSchedule(segments) {
  const seen = new Map();
  segments.forEach((s) => {
    if (!seen.has(s.tag)) {
      seen.set(s.tag, {
        tag: s.tag,
        material: s.material,
        kind: s.kind,
        label: s.label,
        thickness_m: s.thickness_m,
      });
    }
  });
  return Array.from(seen.values()).sort((a, b) => a.tag.localeCompare(b.tag));
}
